// src/composables/useNoteManagePermission.js
//
// Fonte única da verdade para "o usuário logado pode gerenciar (editar/excluir)
// esta nota?". Reflete no front o mesmo gate que o backend aplica por nota, tanto
// nos fluxos individuais quanto nos em lote (PUT /notes/bulk-update e DELETE
// /notes) — que são atômicos: uma única nota sem permissão derruba o lote inteiro.
// Centralizar aqui evita que o checkbox de cada nota (NoteList) e o "marcar todas"
// (NoteSection) divirjam, como divergiam antes.
//
// getCtx() deve retornar { userId, textCreatedByUserId, activityCreatedByUserId }.
// É lido a cada chamada de canManageNote/canEditNote; como essas funções são
// invocadas dentro de computeds/templates nos componentes, a reatividade das
// stores e dos props é preservada mesmo sem receber refs (compatível com Vue 3.2,
// que não tem `toValue`).

import { computed } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

export function useNoteManagePermission(getCtx) {
  const userStore = useUserStore();
  const { canModifyNote } = useMemberRestrictions();

  const isSysUser = computed(() => {
    const name =
      userStore.user?.userName ??
      userStore.currentUser?.userName ??
      userStore.user?.username ??
      null;
    return (name || "").toLowerCase() === "sys";
  });

  function hasAnyPermission(entity, ...permissionNames) {
    if (!entity || !Array.isArray(entity.userPermissions)) return false;
    const lowerNames = permissionNames.map((p) => p.toLowerCase());
    return entity.userPermissions.some((perm) =>
      lowerNames.includes(perm.permissionName?.toLowerCase())
    );
  }

  function canEditNote(note) {
    return hasAnyPermission(note, "Editar");
  }

  function canManageNote(note) {
    if (!note) return false;
    const ctx = (typeof getCtx === "function" ? getCtx() : getCtx) || {};
    const { userId, textCreatedByUserId, activityCreatedByUserId } = ctx;
    return (
      isSysUser.value ||
      userId == note.createdByUserId ||
      userId == textCreatedByUserId ||
      userId == activityCreatedByUserId ||
      canEditNote(note)
    );
  }

  // "Esta nota pode ser EDITADA por mim?" — mais estrito que canManageNote, que
  // também abre para quem criou o texto ou a atividade (esses podem apagar e
  // selecionar, não necessariamente editar).
  //
  // ⚠️ É a mesma regra de autoria/permissão do `canShowEditNote` do
  // NoteCrudDropdown.vue, que a monta a partir de props. Enquanto as duas
  // existirem, mudar uma sem a outra faz o lápis de edição no card aparecer onde
  // o menu ••• não oferece "Editar" — ou o contrário. Se for mexer aqui, olhe lá.
  //
  // Diferença deliberada quanto ao VÍNCULO (canModifyNote): no menu ••• o item
  // fica visível e esmaecido quando o vínculo bloqueia; aqui o lápis inline
  // continua oculto, porque é um affordance por campo dentro do card, sem lugar
  // natural para o estado desabilitado. O motivo aparece no menu.
  function canFullyEditNote(note) {
    if (!note) return false;
    const ctx = (typeof getCtx === "function" ? getCtx() : getCtx) || {};
    const { userId, activityCreatedByUserId } = ctx;

    if (!canModifyNote("edit", note.createdByUserId, activityCreatedByUserId)) {
      return false;
    }

    return (
      isSysUser.value ||
      userId == note.createdByUserId ||
      canEditNote(note)
    );
  }

  return {
    isSysUser,
    hasAnyPermission,
    canEditNote,
    canManageNote,
    canFullyEditNote,
  };
}